import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn } from "lucide-react";
import HirafiIcon from "@/components/HirafiIcon";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { DirectionalArrow } from "@/components/DirectionalArrow";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate, useLocalizedPath } from "@/hooks/useLocalizedNavigate";

const ArtisanLogin = () => {
  const { t } = useLanguage("artisan");
  const lNavigate = useLocalizedNavigate();
  const lp = useLocalizedPath();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { data: authData, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      toast({
        title: t("login.toast_error"),
        description: error.message === "Invalid login credentials"
          ? t("login.toast_bad_credentials")
          : error.message,
        variant: "destructive",
      });
    } else if (authData.user) {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", authData.user.id);

      const isArtisan = roles?.some(r => r.role === "artisan_entreprise" || r.role === "artisan_bricoleur");

      if (isArtisan) {
        toast({ title: t("login.toast_success"), description: t("login.toast_success_artisan") });
        lNavigate("/dashboard");
      } else {
        toast({ title: t("login.toast_success"), description: t("login.toast_success_client") });
        lNavigate("/mes-annonces");
      }
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <nav className="sticky top-0 z-50 border-b bg-card/80 backdrop-blur-md">
        <div className="container flex h-16 items-center justify-between">
          <Link to={lp("/")} className="flex items-center gap-2">
            <HirafiIcon size={36} />
            <span className="font-display text-xl font-bold text-foreground">Hirafi</span>
          </Link>
          <Link to={lp("/artisan")}>
            <Button variant="ghost" size="sm">
              <DirectionalArrow direction="left" className="me-1 h-4 w-4" /> {t("login.back_btn")}
            </Button>
          </Link>
        </div>
      </nav>

      <main className="container max-w-md py-16">
        <div className="mb-8 text-center">
          <h1 className="font-display text-3xl font-bold">{t("login.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("login.subtitle")}</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4 rounded-xl border bg-card p-6">
          <div className="space-y-2">
            <Label className="font-display font-semibold">Email</Label>
            <Input
              type="email"
              placeholder="votre@email.dz"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="font-display font-semibold">Mot de passe</Label>
            <Input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <Button variant="hero" size="xl" className="w-full" type="submit" disabled={loading}>
            <LogIn className="me-2 h-5 w-5" />
            {loading ? t("login.submitting") : t("login.submit")}
          </Button>

          <div className="text-center text-sm text-muted-foreground space-y-1">
            <p>
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-primary hover:underline"
              >
                {t("login.forgot_password")}
              </button>
            </p>
            <p>
              {t("login.no_account")}{" "}
              <Link to={lp("/inscription")} className="font-semibold text-primary hover:underline">
                {t("login.create_account")}
              </Link>
            </p>
          </div>
        </form>
      </main>
      <ForgotPasswordDialog
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        defaultEmail={email}
      />
    </div>
  );
};

export default ArtisanLogin;
